import React, { useState } from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import WebSocketFooter from '../shared/WebSocketFooter';
import IncidentDetailModal from './IncidentDetailModal';
import AIRecommendationModal from './AIRecommendationModal';
import { formatRelativeTime } from '../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getIncidents = async () => {
  const response = await fetch(`${BACKEND_URL}/api/incidents`);
  if (!response.ok) throw new Error('Failed to fetch incidents');
  return response.json();
};

const IncidentsPage = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  const { data: incidents, loading, error, refetch } = useDataFetching(getIncidents, []);

  if (loading) return <LoadingState message="Loading incidents..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  // Calculate stats
  const totalIncidents = incidents?.length || 0;
  const newIncidents = incidents?.filter(i => i.state === 'New').length || 0;
  const inProgressIncidents = incidents?.filter(i => i.state === 'In Progress').length || 0;
  const aiAssignedCount = incidents?.filter(i => i.assigned_to && i.agent_id).length || 0;
  const aiRate = totalIncidents > 0 ? Math.round((aiAssignedCount / totalIncidents) * 100) : 0;

  // Apply filters
  const filteredIncidents = incidents?.filter(incident => {
    if (statusFilter !== 'all' && incident.state !== statusFilter) return false;
    if (priorityFilter !== 'all' && !incident.priority.includes(priorityFilter)) return false;
    if (categoryFilter !== 'all' && incident.category !== categoryFilter) return false;
    return true;
  }) || [];

  const clearFilters = () => {
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
  };

  const handleViewIncident = (incident) => {
    setSelectedIncident(incident);
    setShowDetailModal(true);
  };

  const handleAIRecommendation = (incident) => {
    setSelectedIncident(incident);
    setShowAIModal(true);
  };

  const closeModals = () => {
    setShowDetailModal(false);
    setShowAIModal(false);
    setSelectedIncident(null);
  };

  const getPriorityColor = (priority) => {
    if (priority.includes('Critical')) return 'bg-red-100 text-red-700 border border-red-300';
    if (priority.includes('High')) return 'bg-orange-100 text-orange-700 border border-orange-300';
    if (priority.includes('Moderate')) return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
    return 'bg-blue-100 text-blue-700 border border-blue-300';
  };

  const getStateColor = (state) => {
    if (state === 'Resolved') return 'bg-green-100 text-green-700 border border-green-300';
    if (state === 'In Progress') return 'bg-blue-100 text-blue-700 border border-blue-300';
    return 'bg-gray-100 text-gray-700 border border-gray-300';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Incident Management</h1>
          <p className="text-gray-600 mt-1">ServiceNow incidents with AI-powered recommendations</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Incidents</div>
                <div className="text-2xl font-bold text-gray-900">{totalIncidents}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">New (24h)</div>
                <div className="text-2xl font-bold text-gray-900">{newIncidents}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">In Progress</div>
                <div className="text-2xl font-bold text-gray-900">{inProgressIncidents}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">AI Assigned</div>
                <div className="text-2xl font-bold text-gray-900">{aiAssignedCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">AI Rate</div>
                <div className="text-2xl font-bold text-gray-900">{aiRate}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="New">New</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Moderate">Moderate</option>
              <option value="Low">Low</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="Database">Database</option>
              <option value="API">API</option>
              <option value="Performance">Performance</option>
              <option value="Security">Security</option>
              <option value="Cost">Cost</option>
            </select>

            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Incident
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredIncidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-blue-600">{incident.number}</div>
                      <div className="text-sm text-gray-600 mt-1">{incident.short_description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${getPriorityColor(incident.priority)}`}>
                        {incident.priority.split(' - ')[0]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${getStateColor(incident.state)}`}>
                        {incident.state}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {incident.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {incident.agent_id && <span className="text-green-600 text-sm">AI Agent</span>}
                        <span className="text-sm text-gray-900">{incident.assigned_to || 'Unassigned'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(incident.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleViewIncident(incident)}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          View
                        </button>
                        {incident.agent_id && (
                          <button 
                            onClick={() => handleAIRecommendation(incident)}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            AI
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* WebSocket Footer */}
        <WebSocketFooter />

        {/* Modals */}
        <IncidentDetailModal
          incident={selectedIncident}
          isOpen={showDetailModal}
          onClose={closeModals}
        />
        <AIRecommendationModal
          incident={selectedIncident}
          isOpen={showAIModal}
          onClose={closeModals}
        />
      </div>
    </div>
  );
};

export default IncidentsPage;
